import { useState, useEffect, useContext } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "../styling/App.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import axios from "axios";
import { EventDataContext } from "./EventDataContext";
import { createPortal } from "react-dom";

const localizer = momentLocalizer(moment);

const EventWithHover = ({ event }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    
    // 1. DYNAMIC WIDTH MATCH: Calculate exactly how many pixels 22% of the screen is
    const tooltipWidth = window.innerWidth * 0.22; 
    const tooltipHeight = 280; // Estimated height

    let x = rect.left + window.scrollX;
    let y = rect.bottom + window.scrollY;

    // 2. RIGHT BOUNDARY CHECK: If it goes off the right edge (leaving a 20px safety gap)
    if (x + tooltipWidth > window.innerWidth - 20) {
      // Pin it 20px away from the right edge of the window, instead of the event's edge
      x = window.innerWidth - tooltipWidth - 20;
    }

    // 3. BOTTOM BOUNDARY CHECK: If it goes off the bottom of the screen
    if (rect.bottom + tooltipHeight > window.innerHeight) {
      y = (rect.top + window.scrollY) - tooltipHeight;
    }

    setMousePos({ x, y });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const tooltip = (
    <div
      style={{
        position: "absolute",
        top: mousePos.y + 1,
        left: mousePos.x + 1,
        backgroundColor: "white",
        padding: "15px",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        zIndex: 9999,
        width: "22%",
        height: "auto", // FIX: Let the box grow with its content instead of hardcoding 250px
        boxSizing: "border-box", // FIX: Stops padding from pushing the button out
        border: "1px solid #ccc",
        pointerEvents: "auto",
      }}
    >
      <h2
        style={{
          fontSize: "20px",
          display: "flex",
          justifyContent: "center",
          marginTop: "-5px",
          color: "#2a1980",
          textAlign: "center"
        }}
      >
        {event?.title || ""}
      </h2>

      <div style={{ display: "flex", gap: "25px", marginBottom: "15px" }}>
        <div style={{ width: "50%" }}>
          <h4
            style={{
              fontSize: "15px",
              marginTop: "0px",
              marginBottom: "5px",
              borderTop: "1px solid #f0f0f0",
              color: "#2a1980",
              paddingTop: "10px"
            }}
          >
            Booking Overview
          </h4>
          <p style={{ color: "#625C73", margin: "0px" }}>{event?.originalEvent?.details}</p>
        </div>

        <div style={{ width: "50%" }}>
          <h4
            style={{
              fontSize: "15px",
              marginTop: "0px",
              marginBottom: "5px",
              color: "#2a1980",
            }}
          >
            Date
          </h4>
          <p style={{ color: "#080700", margin: "0px" }}>
            {event?.start.toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </p>

          <h4
            style={{
              fontSize: "15px",
              marginTop: "10px",
              marginBottom: "5px",
              color: "#2a1980",
            }}
          >
            Time
          </h4>
          <p style={{ color: "#625C73", margin: "0px" }}>
            {event?.start.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            -{" "}
            {event?.end.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>

          <h4
            style={{
              fontSize: "15px",
              marginTop: "10px",
              marginBottom: "5px",
              color: "#2a1980",
            }}
          >
            Location
          </h4>
          <p style={{ color: "#625C73", margin: "0px" }}>
            {event?.originalEvent?.address || ""}
          </p>
        </div>
      </div>

      <button
        style={{
          fontSize: "15px",
          background: "transparent linear-gradient(90deg, #1C1154 0%, #211074 47%, #260F99 100%) 0% 0% no-repeat padding-box",
          border: "none",
          borderRadius: "4px",
          color: "white",
          display: "flex",
          justifyContent: "center",
          padding: "10px",
          width: "100%",
          cursor: "pointer",
          boxSizing: "border-box" // FIX: Prevents button from overflowing the container
        }}
      >
        View Booking Details
      </button>
    </div>
  );

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: "relative", zIndex: 1 }}
    >
      <div style={{ fontSize: "12px", fontWeight: "500" }}>{event.title}</div>
      {showTooltip && createPortal(tooltip, document.body)}
    </div>
  );
};

const MyCalendar = ({ onEventClick }) => {
  const [events, setEvents] = useState([]);
  const { setEventData } = useContext(EventDataContext);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/business/events`,
        {
          withCredentials: true,
        }
      );
      const formatted = res.data.data.map((event) => ({
        id: event._id,
        title: event.eventName,
        start: new Date(event.startTime),
        end: new Date(event.endTime),

        originalEvent: {
          ...event,
          onClick: onEventClick,
        },
      }));
      setEvents(formatted);
    } catch (err) {
      console.error("Error fetching events:", err);
    }
  };

  const handleEventSelect = (event) => {
    setEventData(event.originalEvent);
    onEventClick(event.id);
  };

  return (
    <div>
      <h1
        style={{
          color: "#000",
          fontWeight: "600",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      >
        Calendar
      </h1>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={["month", "week", "day"]}
        style={{
          height: 500,
          backgroundColor: "#fff",
          marginBottom: "15px",
          marginRight: "15px",
        }}
        onSelectEvent={handleEventSelect}
        components={{
          event: EventWithHover,
        }}
        formats={{
          eventTimeRangeFormat: () => "",
        }}
        eventPropGetter={(event) => {
          return {
            style: {
              background:
                "transparent linear-gradient(90deg, #1C1154 0%, #260F99 100%) 0% 0% no-repeat padding-box",
              color: "white",
              borderRadius: "4px",
              padding: "2px",
              border: "none",
            },
          };
        }}
      />
    </div>
  );
};

export default MyCalendar;